import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, apiRequest } from '@/lib/api/client';

interface ProjectOption {
  id: string;
  name: string;
}

interface CreatedTask {
  id: string;
  number: number;
  title: string;
}

/**
 * Quick capture - journey J1.
 *
 * @remarks
 * The acceptance test is that a non-technical person records work in under two minutes with
 * no training, so the form asks for the least that can possibly work: what it is, and which
 * project. `11-Rollout-and-Adoption.md` section 3 is explicit that nothing beyond title and
 * project may be required - every extra mandatory field is a reason to go back to sending a
 * message instead.
 *
 * Two things this deliberately does **not** do:
 *
 * - **No optimistic close.** The dialog stays open, disabled, until the server confirms.
 *   The write goes to GitLab synchronously, and showing a task as saved before GitLab
 *   accepted it is exactly the lie the architecture exists to prevent
 *   (`frontend-standards.md` section 7).
 * - **No GitLab vocabulary.** Nothing here says issue, repository or milestone. That an
 *   issue is created behind the scenes is an implementation detail, and leaking it to this
 *   user is a defect rather than a rough edge.
 */
export function QuickCaptureDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [projectId, setProjectId] = useState('');
  const [description, setDescription] = useState('');

  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: () =>
      apiRequest<{ items: ProjectOption[] }>('/projects', { query: { page: 1, pageSize: 100 } }),
    enabled: open,
    staleTime: 60_000,
  });

  const create = useMutation({
    mutationFn: () =>
      apiRequest<CreatedTask>('/tasks', {
        method: 'POST',
        body: {
          projectId,
          title: title.trim(),
          description: description.trim() || null,
        },
      }),
    onSuccess: () => {
      // Refetched rather than patched into the cache. The server decides the inbox order,
      // and inserting locally would put the new task wherever the client guessed.
      void queryClient.invalidateQueries({ queryKey: ['my-work'] });
      reset();
      onClose();
    },
  });

  function reset() {
    setTitle('');
    setProjectId('');
    setDescription('');
    create.reset();
  }

  const canSubmit = title.trim().length > 0 && projectId.length > 0 && !create.isPending;

  return (
    <Dialog
      open={open}
      onClose={create.isPending ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      // Full screen on a phone. A dialog with two inputs and a keyboard open has no room to
      // be a floating card at 380px.
      sx={{ '& .MuiDialog-paper': { m: { xs: 1, sm: 4 } } }}
    >
      <DialogTitle>ثبت کار جدید</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {create.isError ? (
            <Alert severity="error">{messageFor(create.error)}</Alert>
          ) : null}

          <TextField
            label="عنوان"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            autoFocus
            required
            disabled={create.isPending}
            helperText="در یک جمله بنویسید چه کاری باید انجام شود."
          />

          <TextField
            label="پروژه"
            select
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
            required
            disabled={create.isPending || projects.isPending}
            helperText={
              projects.data?.items.length === 0
                ? 'هنوز پروژه‌ای برای شما تعریف نشده است.'
                : 'کار در کدام پروژه ثبت شود؟'
            }
          >
            {(projects.data?.items ?? []).map((project) => (
              <MenuItem key={project.id} value={project.id}>
                {project.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="توضیح (اختیاری)"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            multiline
            minRows={3}
            disabled={create.isPending}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={create.isPending}>
          انصراف
        </Button>

        <Button variant="contained" onClick={() => create.mutate()} disabled={!canSubmit}>
          {/* The pending label is honest about what is happening: the request is not local. */}
          {create.isPending ? 'در حال ثبت…' : 'ثبت'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * A localized message per error code.
 *
 * The server's own text is not rendered: it is not guaranteed free of GitLab vocabulary, and
 * this dialog is the one screen a non-technical person uses most.
 */
function messageFor(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'ارتباط با سرور برقرار نشد. کار ثبت نشد؛ دوباره تلاش کنید.';
  }

  switch (error.errorCode) {
    case 'GitLab.Unavailable':
      return 'سامانه مرجع در دسترس نیست، بنابراین کار ثبت نشد. کمی بعد دوباره تلاش کنید.';

    case 'Auth.GitLabCredentialUnavailable':
      return 'نشست شما پایان یافته است. لطفاً دوباره وارد شوید.';

    case 'Project.NotFound':
      return 'این پروژه در دسترس شما نیست.';

    case 'Validation.Failed':
      return 'عنوان را وارد کنید و یک پروژه انتخاب کنید.';

    default:
      return 'ثبت کار ممکن نشد و چیزی ذخیره نشد.';
  }
}
